import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { groupBy } from 'ramda';
import moment from 'moment';
import isEmptyArray from '../../utils/isEmptyArray';
import LoadingIcon from '../../components/shared/LoadingIcon';
import TimeTag from '../TimeLine/TimeTag';
import LoadMoreClaimEvents from './LoadMoreClaimEvents';
import { loadingStyles, timelineStyles } from './ClaimTimeline.styles';
import ClaimAllAroundGallery from '../../components/ClaimAllAroundGallery';
import ClaimTimelineItem from '../TimeLine/ClaimTimelineItem';
import AllAroundModal from '../../components/AllAroundModal';
import { useIntl } from 'react-intl';
import {
  selectCars,
  selectSelectedCar,
  carDamages as selectCarDamages,
} from '../../redux/store/userData/cars';
import { selectSelectedClaim } from '../../redux/store/userData/claims';
import {
  selectIsAllClaimEventsLoaded,
  selectClaimEvents,
} from '../../redux/store/userData/vehicleEvents';
import { VehicleEventData } from '../../@types';

const ClaimTimeline: React.FC = () => {
  const intl = useIntl();
  const vehicleEvents = useSelector(selectClaimEvents) as any;
  const selectedClaim = useSelector(selectSelectedClaim);
  const selectedCar = useSelector(selectSelectedCar);
  const isAllEventsLoaded = useSelector(selectIsAllClaimEventsLoaded);
  const cars = useSelector(selectCars);
  const damages = useSelector(selectCarDamages);

  // Synthesise carvalooFeedback rows only for carvaloo damages whose
  // trip is currently visible in the timeline.
  const visibleTripIds = new Set<string>();
  (vehicleEvents || []).forEach((e: any) => {
    if (e?.tripId) visibleTripIds.add(e.tripId);
  });

  const carvalooFeedbackEvents =
    damages && visibleTripIds.size > 0
      ? damages.flatMap((d: any) => {
          const dTripId = d?.assignedTripId || d?.tripId;
          if (!dTripId || !visibleTripIds.has(dTripId)) return [];
          const dClaimedAt = d?.addedAt ? new Date(d.addedAt).getTime() : null;
          return (d?.events || [])
            .filter((e: any) => e?.type === 'feedback')
            .filter((e: any) => {
              // Drop feedback events that pre-date the damage itself.
              // The /events endpoint can return historic carvaloo
              // feedback events for the same vehicle that aren't
              // semantically related to this damage.
              if (dClaimedAt == null) return true;
              const t = new Date(e?.created).getTime();
              return !Number.isNaN(t) && t >= dClaimedAt;
            })
            .map((e: any) => ({
              type: 'carvalooFeedback',
              addedAt: e.created,
              damageId: d.damageId,
              damageEntityId: d.id,
              tripId: dTripId,
              presentableTripId: d.tripPresentableId,
              geoLocation: d.geoLocation,
              mileage: d.mileage,
              // Always use our S3 copy — carvaloo blob URLs expire.
              images: d.damageImages,
              eventInfo: e.comments,
              carvalooEventUrl: d.carvalooEventUrl,
            }));
        })
      : [];

  const VIN = cars?.find(
    (car) => car.id === selectedClaim?.carId || selectedCar?.id
  )?.chassisNumber;
  const [isVisibleCarCheckGallery, setIsVisibleCarCheckGallery] =
    React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedAllAroundIndex, setSelectedAllAroundIndex] = React.useState(0);
  const [serviceImages, setServiceImages] = useState([]);
  const [isVisibleServiceModal, setIsVisibleServiceModal] = useState(false);
  let tripId: any;
  let isInsideTrip = false;
  const serviceImageTitle = intl.formatMessage({
    id: 'fleetadmin.trip.serviceImage.modal.title',
  });
  if (isEmptyArray(vehicleEvents) && carvalooFeedbackEvents.length === 0) {
    return (
      <>
        <div className="wrapper">
          <LoadingIcon />
        </div>
        <style jsx>{loadingStyles}</style>
      </>
    );
  }

  let groupedVehicleEvents = {
    [moment(selectedClaim?.tripStartedAt || selectedClaim?.addedAt).format(
      'YYYY-MM-DD'
    )]: [],
  } as any;

  const allEvents = [...(vehicleEvents || []), ...carvalooFeedbackEvents];
  if (!isEmptyArray(allEvents)) {
    groupedVehicleEvents = groupBy((ve: any) =>
      moment(ve?.addedAt).format('YYYY-MM-DD')
    )(allEvents);
    Object.values(groupedVehicleEvents).forEach((arr: any) =>
      arr.sort(
        (a: any, b: any) =>
          new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
      )
    );
  }

  const galleryData =
    vehicleEvents !== null
      ? vehicleEvents
          .filter(
            (event: { type: string; imageType: string | string[] }) =>
              event.type === 'imageAdded' &&
              !event.imageType.includes('service')
          )
          .map(
            (event: {
              imageType: any;
              presentableTripId: any;
              addedAt: any;
              images: any;
            }) => {
              return {
                type: event.imageType,
                tripId: event.presentableTripId,
                addedAt: event.addedAt,
                images: event.images,
              };
            }
          )
      : [];

  const openCarCheckGallery = (addedAt: any) => {
    const indexWithinGallery = galleryData.findIndex(
      (data: { addedAt: any }) => data.addedAt === addedAt
    );
    setSelectedAllAroundIndex(indexWithinGallery);
    setIsVisibleCarCheckGallery(true);
  };

  const closeCarCheckGallery = () => {
    setIsVisibleCarCheckGallery(false);
  };

  const openServiceModal = (ve: any) => {
    setServiceImages(ve.images);
    setIsVisibleServiceModal(true);
  };

  const closeServiceModal = () => {
    setIsVisibleServiceModal(false);
  };

  return (
    <>
      <div className="timeline-wrapper">
        {Object.entries(groupedVehicleEvents).map(
          ([key, vehicleEvents]: any, i) => {
            return (
              <div key={`timeline-${key}`} className="timeline-group">
                <TimeTag
                  date={key}
                  hideFirstBar={i === 0}
                  isInsideTrip={isInsideTrip}
                />
                {vehicleEvents?.map((ve: VehicleEventData, _i: number) => {
                  let isNewTrip =
                    ve.tripId !== undefined && tripId !== ve.tripId;
                  if (isNewTrip) {
                    tripId = ve.tripId;
                  }
                  isInsideTrip = !['tripStarted', 'shockDetected'].includes(
                    ve?.type!
                  );

                  let hideSecondBar =
                    isAllEventsLoaded &&
                    vehicleEvents.length - 1 === _i &&
                    Object.keys(groupedVehicleEvents).length - 1 === i;

                  return (
                    <ClaimTimelineItem
                      key={`vehicleEvents-${_i}`}
                      isNewTrip={isNewTrip}
                      vehicleEvent={ve}
                      hideSecondBar={hideSecondBar}
                      isInsideTrip={ve.type === 'tripStarted' || isInsideTrip}
                      onClickBody={() =>
                        ve?.imageType?.includes('service')
                          ? openServiceModal(ve)
                          : openCarCheckGallery(ve.addedAt)
                      }
                    />
                  );
                })}
              </div>
            );
          }
        )}
        <LoadMoreClaimEvents />
      </div>
      {galleryData && galleryData.length > 0 && (
        <ClaimAllAroundGallery
          vin={VIN}
          galleryData={galleryData}
          visible={isVisibleCarCheckGallery}
          onClose={closeCarCheckGallery}
        />
      )}
      {serviceImages && serviceImages.length > 0 && (
        <AllAroundModal
          allAroundCheck={{
            images: serviceImages,
            title: serviceImageTitle,
            chassisNumber: ' ',
          }}
          visible={isVisibleServiceModal}
          onClose={closeServiceModal}
        />
      )}
      <style jsx>{timelineStyles}</style>
    </>
  );
};

export default ClaimTimeline;
